// CreateProjectModal.jsx
import React, { useEffect, useState, useCallback } from "react";

import axiosInstace from "../../axiosInstace";
import "./CreateProject.scss";
import EmployeeTable from "../../components/EmployeeTable/EmployeeTable";
import InputField from "../../components/InputComponent/InputField";
import FancyRadioGroup from "../../components/fancy-radio-group/fancy-radio-group";
import CheckList from "../../components/CheckList/CheckList";
import AddButton from "../../components/AddButton";
import RemoveButton from "../../components/RemoveButton/RemoveButton";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import Toast from "../../components/Toast/Toast";
import { createPortal } from "react-dom";

const ROLES = [
  { id: 1, name: "APPROVER", permissions: [] },
  { id: 2, name: "CREATOR", permissions: [] },
  { id: 3, name: "DOER", permissions: [] },
  { id: 4, name: "VIEWER", permissions: [] },
  { id: 5, name: "CLIENT", permissions: [] },
];

const STEP_ORDER = ["details", "category", "members"];

export default function CreateProjectModal({
  onClose = () => {},
  creatorEmpId = null,
  onSuccess = () => {},
  onRefresh = () => {},
}) {
  const BACKEND_BASE =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8082";

  // steps
  const [activeStep, setActiveStep] = useState("details");

  // project details
  const [projectTitle, setProjectTitle] = useState("");
  const [description, setDescription] = useState("");

  const today = new Date();
  const defaultStart = today.toISOString().split("T")[0];
  const defaultEnd = new Date(today.setDate(today.getDate() + 7))
    .toISOString()
    .split("T")[0];

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [projectStatus, setProjectStatus] = useState("PLANNED");
  const [priority, setPriority] = useState("MEDIUM");
  const [remark, setRemark] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    type: "info",
    message: "",
  });

  // category
  const [categoryMode, setCategoryMode] = useState("predefined");
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [availableStages, setAvailableStages] = useState([]);
  const [stages, setStages] = useState([]);

  // inline add form state
  const [newStageName, setNewStageName] = useState("");
  const [newStageFlag, setNewStageFlag] = useState("");

  // members
  const [members, setMembers] = useState([]);

  // UI
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const STATUS_OPTIONS = [
    "PLANNED",
    "ACTIVE",
    "ON_HOLD",
    "PROGRESS",
    "COMPLETED",
    "CANCELLED",
  ];
  const PRIORITY_OPTIONS = ["Low", "Medium", "High"];
  const FLAG_OPTIONS = [
    "Yts",
    "Progress",
    "Waiting",
    "On Hold",
    "Completed",
    "Aborted",
  ];

  const toTitle = (u) => {
    if (typeof u !== "string" || !u.length) return u;
    return u
      .replace(/_/g, " ")
      .toLowerCase()
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
      .join(" ");
  };

  // fetch categories
  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await axiosInstace.get(
          `${BACKEND_BASE}/api/emp/category/allcategory/stagespositions/stage`,
        );
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setCategories(list);
      } catch (err) {
        try {
          const res2 = await axiosInstace.get(
            `${BACKEND_BASE}/api/emp/employee/getall`,
          );
          const list2 = Array.isArray(res2?.data?.data) ? res2.data.data : [];
          setCategories(list2);
        } catch {
          setCategories([]);
        }
      }
    }
    if (categoryMode === "predefined") fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BACKEND_BASE, categoryMode]);

  // fetch available stages
  useEffect(() => {
    async function fetchStages() {
      try {
        const res = await axiosInstace.get(`${BACKEND_BASE}/api/emp/stages`);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setAvailableStages(list);
      } catch {
        setAvailableStages([]);
      }
    }
    if (categoryMode === "custom") fetchStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BACKEND_BASE, categoryMode]);

  const showToast = (type, msg) => {
    setToast({ show: true, type: type || "info", message: String(msg || "") });
  };

  const addManualStageViaForm = () => {
    const name = (newStageName || "").trim();
    if (!name) {
      showToast("error", "Stage name is required.");
      setError("Stage name is required.");
      return;
    }
    if (!newStageFlag) {
      showToast("error", "Please select a stage flag.");
      return;
    }

    const flag = (newStageFlag || "").toString().trim().toUpperCase();
    const norm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");

    setStages((prev) => {
      const exists = prev.some((s) => norm(s.stageName) === norm(name));
      if (exists) {
        showToast("error", `Stage "${name}" already exists.`);
        setError(`Stage "${name}" already exists.`);
        return prev;
      }
      const next = [
        ...prev,
        {
          stageName: name,
          stageFlag: flag,
          stagePositionRequestDTOList: [
            {
              position: prev.length + 1,
              initial: prev.length === 0,
              final: false,
            },
          ],
        },
      ];
      showToast("success", `Added stage "${name}".`);
      setMessage(`Added stage "${name}".`);
      setError("");
      return next;
    });

    setNewStageName("");
    setNewStageFlag("");
  };

  const addStageFromTemplateAdjusted = (st) => {
    if (!st) return;
    const norm = (s) =>
      (s || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    const tplId = st.stageId ?? st.id ?? null;
    const tplName = (st.stageName || st.stageTitle || "").toString().trim();
    const tplFlag = (st.stageFlag || "").toString().trim();
    if (!tplName) return;

    setStages((prev) => {
      let existingIndex =
        tplId != null
          ? prev.findIndex((s) => {
              const sid = s.stageId ?? s.id ?? null;
              return sid != null && sid === tplId;
            })
          : -1;
      if (existingIndex < 0) {
        existingIndex = prev.findIndex(
          (s) => norm(s.stageName) === norm(tplName),
        );
      }
      if (existingIndex >= 0) {
        showToast("error", `Stage "${tplName}" already exists.`);
        setError(`Stage "${tplName}" already exists.`);
        return prev;
      }
      const next = [
        ...prev,
        {
          stageName: tplName,
          stageFlag: tplFlag,
          stageId: tplId ?? undefined,
          stagePositionRequestDTOList: [
            {
              position: prev.length + 1,
              initial: prev.length === 0,
              final: false,
            },
          ],
        },
      ];
      showToast("success", `Added stage "${tplName}".`);
      setMessage(`Added stage "${tplName}".`);
      setError("");
      return next;
    });
  };

  const removeStage = (idx) =>
    setStages((prev) => {
      const afterRemoval = prev.filter((_, i) => i !== idx);
      const resequenced = afterRemoval.map((s) => ({
        ...s,
        stagePositionRequestDTOList: (s.stagePositionRequestDTOList || []).map(
          (p, i) => ({
            ...p,
            position: i + 1,
          }),
        ),
      }));
      const allPositions = resequenced.flatMap(
        (s) => s.stagePositionRequestDTOList || [],
      );
      const hasInitial = allPositions.some((p) => p.initial);
      if (
        !hasInitial &&
        resequenced.length &&
        (resequenced[0].stagePositionRequestDTOList || []).length
      ) {
        resequenced[0].stagePositionRequestDTOList[0].initial = true;
      }
      showToast("info", "Stage removed.");
      setMessage("Stage removed.");
      setError("");
      return resequenced;
    });

  const areMembersEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every(
      (m, i) =>
        m.empId === b[i].empId &&
        m.roleId === b[i].roleId &&
        m.empName === b[i].empName,
    );
  };

  const handleMembersUpdate = useCallback((membersArray) => {
    const next = (membersArray || []).map((m) => ({
      empId: Number(m.empId),
      roleId: Number(m.roleId),
      empName: m.empName,
      roleName: (m.roleName || "").toString().trim().toUpperCase(),
    }));
    setMembers((prev) => (areMembersEqual(prev, next) ? prev : next));
  }, []);

  function buildPayload() {
    const uniqueById = [];
    const seen = new Set();
    (members || [])
      .filter((m) => String(m.empId) !== String(creatorEmpId))
      .forEach((m) => {
        const id = Number(m.empId);
        if (!seen.has(id)) {
          seen.add(id);
          uniqueById.push(m);
        }
      });

    const memberRoleDTOS = uniqueById.map((m) => ({
      empId: Number(m.empId),
      roleId: Number(m.roleId),
    }));

    const up = (v) =>
      v === undefined || v === null
        ? null
        : String(v).toString().trim().toUpperCase();

    const payload = {
      projectTitle: projectTitle.trim(),
      description: description.trim(),
      startDate: startDate || null,
      endDate: endDate || null,
      memberRoleDTOS,
      projectStatus: up(projectStatus),
      priority: up(priority),
      remark: remark?.trim() || null,
    };

    if (categoryMode === "predefined" && categoryId) {
      let catId = Number(categoryId);
      if (!Number.isFinite(catId)) {
        const found = categories.find(
          (c) =>
            String(c.categoryName).trim().toLowerCase() ===
            String(categoryId).trim().toLowerCase(),
        );
        if (found) catId = Number(found.categoryId);
      }
      payload.category = Number.isFinite(catId) ? { categoryId: catId } : null;
    } else if (categoryMode === "custom") {
      let globalPos = 1;
      payload.category = {
        stageRequestDTOS: (stages || []).map((s) => {
          const posList = (s.stagePositionRequestDTOList || []).length
            ? s.stagePositionRequestDTOList.map((p) => ({
                position: Number(globalPos++),
                final: Boolean(p.final),
                initial: Boolean(p.initial),
              }))
            : [
                {
                  position: Number(globalPos++),
                  final: Boolean(s.final),
                  initial: Boolean(s.initial),
                },
              ];

          return {
            stageName: String(s.stageName || "").trim(),
            stageFlag: s.stageFlag
              ? String(s.stageFlag).trim().toUpperCase().replace(/\s+/g, "_")
              : null,
            stagePositionRequestDTOList: posList,
          };
        }),
      };
    }

    return payload;
  }

  function validateBeforeSubmit() {
    if (!projectTitle.trim()) return "Project title is required.";
    const hasApproverByName = (members || []).some(
      (m) => (m.roleName || "").toString().trim().toLowerCase() === "approver",
    );
    const approverRoleIdFallback =
      ROLES.find((r) => r.name === "APPROVER")?.id || 1;
    const hasApproverById = (members || []).some(
      (m) => Number(m.roleId) === Number(approverRoleIdFallback),
    );
    if (!hasApproverByName && !hasApproverById)
      return "At least one Approver is required.";

    const ids = (members || []).map((m) => Number(m.empId));
    const dup = ids.filter((v, i) => ids.indexOf(v) !== i);
    if (dup.length)
      return `Duplicate member selected (empId: ${[...new Set(dup)].join(", ")}).`;

    if (categoryMode === "custom") {
      if (!stages.length)
        return "At least one stage is required for custom category.";
      for (let s of stages) {
        if (!s.stageName || !String(s.stageName).trim())
          return "Each custom stage must have a valid stage name.";
        if (!s.stageFlag || !String(s.stageFlag).trim())
          return "Each custom stage must have a valid stage flag.";
        if (
          !s.stagePositionRequestDTOList ||
          !s.stagePositionRequestDTOList.length
        )
          return `Stage "${s.stageName}" must have at least one position.`;
        for (let p of s.stagePositionRequestDTOList) {
          if (
            p.position === undefined ||
            p.position === null ||
            !Number.isFinite(Number(p.position))
          )
            return `Stage "${s.stageName}" has an invalid position value.`;
        }
      }
      if (stages[0]?.stagePositionRequestDTOList?.some((p) => p.final))
        return "The first stage cannot be final.";
      if (
        stages[stages.length - 1]?.stagePositionRequestDTOList?.some(
          (p) => p.initial,
        )
      )
        return "The last stage cannot be initial.";

      const allPositions = stages.flatMap((s) =>
        (s.stagePositionRequestDTOList || []).map((p) => ({
          ...p,
          stageName: s.stageName,
        })),
      );
      for (let p of allPositions) {
        if (p.initial && p.final)
          return `Stage "${p.stageName}" cannot have a position that is both initial and final.`;
      }
      if (allPositions.filter((p) => p.initial).length > 1)
        return "Only one initial position is allowed.";
      if (allPositions.filter((p) => p.final).length > 1)
        return "Only one final position is allowed.";
    }

    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage("");
    setError("");

    const v = validateBeforeSubmit();
    if (v) {
      showToast("error", v);
      setError(v);
      return;
    }

    const payload = buildPayload();
    setLoading(true);

    try {
      const res = await axiosInstace.post(
        `${BACKEND_BASE}/api/emp/project/create`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );
      const successMsg = res?.data?.message || "Project created successfully.";
      showToast("success", successMsg);
      setMessage(successMsg);
      setError("");

      setProjectTitle("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setProjectStatus("PLANNED");
      setPriority("MEDIUM");
      setRemark("");
      setMembers([]);
      setCategoryId("");
      setStages([]);

      setTimeout(() => {
        try {
          if (typeof onClose === "function") onClose();
        } catch {}
      }, 700);
    } catch (err) {
      const errMsg =
        err?.response?.data?.message || err.message || "Create failed";
      showToast("error", errMsg);
      setError(errMsg);
      setMessage("");
    } finally {
      setLoading(false);
    }
  }

  const handleToggleInitial = (stageIdx, posIdx, checked) => {
    if (!checked) {
      setStages((prev) =>
        prev.map((s, si) =>
          si === stageIdx
            ? {
                ...s,
                stagePositionRequestDTOList: s.stagePositionRequestDTOList.map(
                  (p, pi) => (pi === posIdx ? { ...p, initial: false } : p),
                ),
              }
            : s,
        ),
      );
      return;
    }
    setStages((prev) =>
      prev.map((s, si) => ({
        ...s,
        stagePositionRequestDTOList: s.stagePositionRequestDTOList.map(
          (p, pi) => {
            if (si === stageIdx && pi === posIdx)
              return { ...p, initial: true, final: false };
            return { ...p, initial: false };
          },
        ),
      })),
    );
  };

  const handleToggleFinal = (stageIdx, posIdx, checked) => {
    if (!checked) {
      setStages((prev) =>
        prev.map((s, si) =>
          si === stageIdx
            ? {
                ...s,
                stagePositionRequestDTOList: s.stagePositionRequestDTOList.map(
                  (p, pi) => (pi === posIdx ? { ...p, final: false } : p),
                ),
              }
            : s,
        ),
      );
      return;
    }
    setStages((prev) =>
      prev.map((s, si) => ({
        ...s,
        stagePositionRequestDTOList: s.stagePositionRequestDTOList.map(
          (p, pi) => {
            if (si === stageIdx && pi === posIdx)
              return { ...p, final: true, initial: false };
            return { ...p, final: false };
          },
        ),
      })),
    );
  };

  return createPortal(
    <div className="cp-modal-overlay">
      <div className="cp-modal">
        <button
          type="button"
          className="cp-close-top"
          aria-label="Close create project modal"
          onClick={() => onClose && onClose()}
        >
          ✕
        </button>

        <aside className="cp-sidebar">
          <h2>Create Project</h2>
          <ul className="sidebar-menu">
            {STEP_ORDER.map((step) => (
              <li
                key={step}
                className={`sidebar-item ${activeStep === step ? "active" : ""}`}
                onClick={() => setActiveStep(step)}
              >
                <span className="sidebar-item-dot" />
                {step === "details"
                  ? "Project Details"
                  : step === "category"
                    ? "Category"
                    : "Members & Roles"}
              </li>
            ))}
          </ul>
        </aside>

        <main className="cp-content">
          <form onSubmit={handleSubmit}>
            {/* Project Details */}
            <section
              style={{ display: activeStep === "details" ? "block" : "none" }}
            >
              <div className="form-stack">
                <InputField
                  label="Project Title *"
                  id="projectTitle"
                  type="text"
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  className="input-modern"
                />
                <InputField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  label="Description"
                  name="description"
                  placeholder="Write your description here."
                />

                <div className="cp-date-row">
                  <div className="cp-date-col">
                    <label>Start Date</label>
                    <InputField
                      className="input-modern"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="cp-date-col">
                    <label>End Date</label>
                    <InputField
                      className="input-modern"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="cp-inline-row">
                  <div className="field status-pills">
                    <label className="field-label">Status</label>
                    <div
                      className="pill-row"
                      role="list"
                      aria-label="Project status"
                    >
                      {STATUS_OPTIONS.filter(Boolean).map((opt) => {
                        const val = String(opt).trim();
                        const isSelected = projectStatus === val;
                        const safeVariantClass = `status-${val.toLowerCase().replace(/_/g, "-")}`;
                        return (
                          <button
                            key={val}
                            type="button"
                            data-opt={val}
                            className={`status-pill ${safeVariantClass} ${isSelected ? "selected" : ""}`}
                            aria-pressed={isSelected}
                            onClick={() => setProjectStatus(val)}
                          >
                            {toTitle(val)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="field priority-pills">
                    <label className="field-label">Priority</label>
                    <div
                      className="pill-row"
                      role="list"
                      aria-label="Project priority"
                    >
                      {PRIORITY_OPTIONS.filter(Boolean).map((opt) => {
                        const val = String(opt).trim();
                        const isSelected = priority === val;
                        return (
                          <button
                            key={val}
                            type="button"
                            className={`createtaskprioritybadge ${val.toLowerCase()} ${isSelected ? "selected" : ""}`}
                            aria-pressed={isSelected}
                            onClick={() => setPriority(val)}
                          >
                            {toTitle(val)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <InputField
                  label="Remark (optional)"
                  id="remark"
                  as="textarea"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  className="textarea-modern"
                />
              </div>
            </section>

            {/* Category */}
            <section
              style={{ display: activeStep === "category" ? "block" : "none" }}
            >
              <div className="field">
                <label>Category Mode</label>
                <FancyRadioGroup
                  name="categoryMode"
                  options={[
                    { value: "predefined", label: "Predefined" },
                    { value: "custom", label: "Custom" },
                  ]}
                  value={categoryMode}
                  onChange={(v) => setCategoryMode(v)}
                  direction="row"
                />
              </div>

              {categoryMode === "predefined" && (
                <div>
                  <div
                    className="field category-pills"
                    style={{ marginBottom: 12 }}
                  >
                    <label className="field-label">Select Category</label>
                    <div
                      className="pill-row"
                      role="list"
                      aria-label="Categories"
                    >
                      {(Array.isArray(categories) ? categories : [])
                        .filter(Boolean)
                        .filter(
                          (c) =>
                            Array.isArray(c?.stageDTOList) &&
                            c.stageDTOList.length > 0,
                        ) // 👈 add this
                        .map((c) => {
                          const id = c?.categoryId ?? c?.value ?? c?.id ?? null;
                          const name =
                            c?.categoryName ??
                            c?.categoryTitle ??
                            c?.label ??
                            `Category ${id ?? ""}`;
                          const val = String(id ?? "");
                          const isSelected = String(categoryId ?? "") === val;
                          return (
                            <button
                              key={val || name}
                              type="button"
                              className={`category-pill ${isSelected ? "selected" : ""}`}
                              aria-pressed={isSelected}
                              data-cat={val}
                              onClick={() =>
                                setCategoryId(isSelected ? "" : val)
                              }
                            >
                              {toTitle(name)}
                            </button>
                          );
                        })}
                      {(!Array.isArray(categories) ||
                        categories.length === 0) && (
                        <div className="muted">No categories available</div>
                      )}
                    </div>
                  </div>

                  {categoryId &&
                    (() => {
                      const chosen = categories.find(
                        (c) => String(c?.categoryId) === String(categoryId),
                      );
                      const rawStageList =
                        chosen?.stageDTOList ||
                        chosen?.stageResponseDTOList ||
                        chosen?.stageDTOList ||
                        chosen?.stagePositions ||
                        [];
                      const normalizedStages = (rawStageList || [])
                        .map((st, idx) => {
                          const positionsArr =
                            st.stagePositionResponseDTOList ||
                            st.stagePositionDTOList ||
                            st.stagePositions ||
                            st.stagePositionDTO ||
                            [];
                          const cleaned = (
                            Array.isArray(positionsArr) ? positionsArr : []
                          )
                            .map((p) => {
                              if (!p) return null;
                              const rawPos =
                                p.position ??
                                p.pos ??
                                p.positionIndex ??
                                p.positionNumber ??
                                null;
                              const posNum = Number(rawPos);
                              if (!Number.isFinite(posNum)) return null;
                              return {
                                ...p,
                                position: posNum,
                                initial: Boolean(p.initial),
                                final: Boolean(p.final || p.finalPosition),
                              };
                            })
                            .filter(Boolean);
                          const byPos = new Map();
                          cleaned.forEach((p) => {
                            if (!byPos.has(p.position))
                              byPos.set(p.position, p);
                          });
                          const uniqueSorted = Array.from(byPos.values()).sort(
                            (a, b) => a.position - b.position,
                          );
                          return {
                            stageId: st.stageId ?? st.id ?? `st-${idx}`,
                            stageTitle:
                              st.stageName ??
                              st.stageTitle ??
                              `Stage ${idx + 1}`,
                            stageFlag: st.stageFlag ?? "",
                            _positions: uniqueSorted,
                            _minPos: uniqueSorted.length
                              ? uniqueSorted[0].position
                              : Infinity,
                          };
                        })
                        .filter((s) => s._positions.length > 0)
                        .sort(
                          (a, b) =>
                            Number(a._minPos || 0) - Number(b._minPos || 0),
                        );

                      return (
                        <div style={{ marginTop: 12 }}>
                          <h4 className="section-subtitle">
                            Stages for:{" "}
                            {chosen?.categoryName ?? chosen?.categoryTitle}
                          </h4>
                          <div style={{ overflowX: "auto", marginTop: 8 }}>
                            <table className="category-stages-table">
                              <thead>
                                <tr>
                                  <th>Stage Name</th>
                                  <th>Stage Flag</th>
                                  <th>Positions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {normalizedStages.map((ns) => {
                                  const posText =
                                    (ns._positions || [])
                                      .map((p) => {
                                        const flags = [];
                                        if (p.initial) flags.push("initial");
                                        if (p.final) flags.push("final");
                                        return `${p.position}${flags.length ? ` (${flags.join(", ")})` : ""}`;
                                      })
                                      .join(", ") || "—";
                                  return (
                                    <tr key={ns.stageId}>
                                      <td>{ns.stageTitle}</td>
                                      <td>{ns.stageFlag}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>
                                        {posText}
                                      </td>
                                    </tr>
                                  );
                                })}
                                {(!normalizedStages ||
                                  normalizedStages.length === 0) && (
                                  <tr>
                                    <td colSpan={3} className="empty-cell">
                                      No stages available for this category.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                </div>
              )}

              {categoryMode === "custom" && (
                <div className="stages-grid">
                  <div className="stages-left">
                    <div className="new-stage-row">
                      <InputField
                        placeholder="Stage name"
                        label={"New Stage"}
                        value={newStageName}
                        onChange={(e) => setNewStageName(e.target.value)}
                        className="input-modern"
                        style={{ flex: 1 }}
                      />
                      <div className="createproject-new-stage-flag-pills">
                        {FLAG_OPTIONS.map((flag) => {
                          const value = flag.toUpperCase();
                          const isActive =
                            newStageFlag?.toUpperCase() === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              className={`createproject-flag-pill ${isActive ? "active" : ""} ${value.toLowerCase().replace(/ /g, "_")}`}
                              onClick={() => setNewStageFlag(value)}
                              aria-pressed={isActive}
                            >
                              {flag}
                            </button>
                          );
                        })}
                      </div>
                      <div className="addbtn">
                        <AddButton
                          onClick={addManualStageViaForm}
                          title="Add Stage"
                          label={""}
                          size={38}
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="alert error" style={{ marginBottom: 10 }}>
                        {error}
                      </div>
                    )}
                    {message && (
                      <div
                        className="alert success"
                        style={{ marginBottom: 10 }}
                      >
                        {message}
                      </div>
                    )}

                    <div className="stages-table-wrap">
                      <table className="custom-stages-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Flag</th>
                            <th>Position</th>
                            <th>Initial</th>
                            <th>Final</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stages.map((s, si) => {
                            const pos = s.stagePositionRequestDTOList[0];
                            return (
                              <tr key={si}>
                                <td>{s.stageName}</td>
                                <td>{s.stageFlag}</td>
                                <td>{pos?.position}</td>
                                <td>
                                  <CheckList
                                    id={`initial-${si}`}
                                    label=""
                                    checked={!!pos?.initial}
                                    disabled={si === stages.length - 1}
                                    onChange={(e) =>
                                      handleToggleInitial(
                                        si,
                                        0,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </td>
                                <td>
                                  <CheckList
                                    id={`final-${si}`}
                                    label=""
                                    checked={!!pos?.final}
                                    disabled={si === 0}
                                    onChange={(e) =>
                                      handleToggleFinal(si, 0, e.target.checked)
                                    }
                                  />
                                </td>
                                <td>
                                  <RemoveButton
                                    onClick={() => removeStage(si)}
                                    tooltip="Remove Stage"
                                    title="Remove Stage"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                          {stages.length === 0 && (
                            <tr>
                              <td colSpan={6} className="empty-cell">
                                No custom stages added yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <aside className="createproject-available-stages">
                    <h4 className="section-subtitle">Available Stages</h4>
                    <div className="createproject-available-table-wrap">
                      <table className="createproject-available-stages-table">
                        <thead>
                          <tr>
                            <th>Stage Name</th>
                            <th>Flag</th>
                            <th>Meta</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableStages.map((st) => {
                            const positionsArr =
                              st.stagePositionDTOList ||
                              st.stagePositionResponseDTOList ||
                              st.stagePositions ||
                              st.stagePositionDTO ||
                              [];
                            const isInitial = positionsArr.some(
                              (p) => p.initial,
                            );
                            const isFinal = positionsArr.some(
                              (p) => p.final || p.finalPosition,
                            );
                            return (
                              <tr
                                key={
                                  st.stageId ||
                                  st.id ||
                                  st.stageTitle ||
                                  st.stageName
                                }
                              >
                                <td className="createproject-stage-name">
                                  {st.stageName || st.stageTitle}
                                </td>
                                <td>
                                  {st.stageFlag ? (
                                    <span
                                      className={`createproject-flag-pill ${st.stageFlag.toLowerCase()}`}
                                    >
                                      {st.stageFlag}
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td>
                                  {isInitial && (
                                    <span className="createproject-meta-pill initial">
                                      Initial
                                    </span>
                                  )}
                                  {isFinal && (
                                    <span className="createproject-meta-pill final">
                                      Final
                                    </span>
                                  )}
                                  {!isInitial && !isFinal && "—"}
                                </td>
                                <td>
                                  <AddButton
                                    onClick={() =>
                                      addStageFromTemplateAdjusted(st)
                                    }
                                    size={26}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                          {availableStages.length === 0 && (
                            <tr>
                              <td
                                colSpan={4}
                                className="createproject-empty-row"
                              >
                                No stages available.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </aside>
                </div>
              )}
            </section>

            {/* Members */}
            <section
              style={{ display: activeStep === "members" ? "block" : "none" }}
            >
              <h3 className="section-title">Members & Roles</h3>
              <EmployeeTable
                onAdd={handleMembersUpdate}
                creatorEmpId={creatorEmpId}
              />
            </section>

            {/* Footer */}
            <div className="cp-footer">
              {activeStep === "details" && (
                <ButtonWithIcon
                  text="Category "
                  color="success"
                  icon="mdi:arrow-right"
                  iconPosition="right"
                  onClick={() => setActiveStep("category")}
                />
              )}
              {activeStep === "category" && (
                <>
                  <ButtonWithIcon
                    text=" Details"
                    icon="mdi:arrow-left"
                    color="warning"
                    onClick={() => setActiveStep("details")}
                  />
                  <ButtonWithIcon
                    text="Members "
                    icon="mdi:arrow-right"
                    color="success"
                    iconPosition="right"
                    onClick={() => setActiveStep("members")}
                  />
                </>
              )}
              {activeStep === "members" && (
                <>
                  <ButtonWithIcon
                    text=" Category"
                    icon="mdi:arrow-left"
                    color="warning"
                    onClick={() => setActiveStep("category")}
                  />
                  <ButtonWithIcon
                    text={loading ? "Creating..." : "Create Project"}
                    icon="mdi:check-circle"
                    type="submit"
                    color="success"
                    iconPosition="right"
                    disabled={loading}
                  />
                </>
              )}
            </div>
          </form>
        </main>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, type: "info", message: "" })}
        />
      )}
    </div>,
    document.body,
  );
}
